import React from 'react';
import FlightParameterControls from './FlightParameterControls';
import ZoneDisplay from './ZoneDisplay';
import DroneZoneMapOverlay from './DroneZoneMapOverlay/DroneZoneMapOverlay';
import { UnitSystem } from '../types/calculator';
import '../styles/calculator.css';  // Regular CSS styles
import '../styles/pulse_drones_branding.css'; // Import official Pulse Drones branding
import '../styles/responsive.css'; // Import responsive styles for different screen sizes
import '../styles/DroneZoneMap.css'; // Import styles for DroneZoneMapOverlay
import styles from '../styles/calculator.module.css';
import { useFlightCalculationsContext } from '../contexts/FlightCalculationsContext';
import logoImage from '../assets/PD_Logo-Horizontal_white_teal.png';

const Calculator: React.FC = () => {
  // Use our context for flight calculations
  const {
    unitSystem,
    zones,
    hasSpaceWarning,
    setUnitSystem
  } = useFlightCalculationsContext();
  
  // Map visualization is now the default and only option
  
  // Google Maps API key - should be from environment variable
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  console.log('API Key available:', !!googleMapsApiKey);
  
  const toggleUnit = (unit: UnitSystem) => {
    setUnitSystem(unit);
  };

  return (
    <div className={styles.calculator} role="main">
      {/* Header */}
      <div className={styles.header}>
        <img 
          src={logoImage} 
          alt="Pulse Drones Logo" 
          className={styles.headerLogo}
          width="280"
        />
        <h2 className={styles.headerTitle}>Restricted Area Calculator</h2>
        
        <div className={styles.unitToggle}>
          <button 
            className={`${styles.unitButton} ${unitSystem === 'metric' ? styles.active : ''}`}
            onClick={() => toggleUnit('metric')}
            aria-label="Switch to metric units"
          >
            Metric (m)
          </button>
          <button 
            className={`${styles.unitButton} ${unitSystem === 'imperial' ? styles.active : ''}`}
            onClick={() => toggleUnit('imperial')}
            aria-label="Switch to imperial units"
          >
            Imperial (ft)
          </button>
        </div>
      </div>
      
      {hasSpaceWarning && (
        <div className={styles.spaceConstraintWarning} role="alert" aria-live="assertive">
          <p>
            <strong>Warning:</strong> The calculated zones exceed the available space.
            Consider adjusting your flight parameters or finding a larger operational area.
          </p>
        </div>
      )}
      
      <div className={styles.mainContent}>
        {/* Flight Parameters Card */}
        <div className={styles.card}>
          <h2>
            <div className={styles.headingIndicator}></div>
            Flight Parameters
          </h2>
          <FlightParameterControls />
        </div>
        
        {/* Operational Zones Card */}
        <div className={styles.card}>
          <h2>
            <div className={styles.headingIndicator}></div>
            Operational Zones
          </h2>
          <ZoneDisplay zones={zones} />
        </div>
      </div>
      
      <div className={styles.visualizationContainer}>
        {/* Map Visualization Card */}
        <div className={styles.card}>
          <h2>
            <div className={styles.headingIndicator}></div>
            Zone Map Overlay
          </h2>
          <p className={styles.mapInstructions}>
            This map shows the drone operational zones based on your calculations. Click on the map to set a new center point or use the control panel for precise positioning.
          </p>
          {googleMapsApiKey ? (
            <DroneZoneMapOverlay zones={zones} apiKey={googleMapsApiKey} />
          ) : (
            <div className={styles.apiKeyError}>
              <h3>Google Maps API Key Required</h3>
              <p>
                To use the Map Visualization, please add your Google Maps API key to your environment variables.
                See the <code>.env.example</code> file for setup instructions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calculator;
